import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, MessageCircle, Calendar, User, Trash2, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AdminMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  created_at: string;
  read: boolean;
  solved: boolean;
}

export const AdminMessagesModal = ({ isOpen, onClose }: AdminMessagesModalProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [activeTab, setActiveTab] = useState<'new' | 'solved'>('new');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchMessages();
    }
  }, [isOpen]);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        setMessages(data || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch messages",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("messages")
        .update({ read: true })
        .eq("id", messageId);

      if (error) {
        if (import.meta.env.DEV) console.error("Error marking message as read:", error);
      } else {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, read: true } : msg
        ));
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error("Error marking message as read:", error);
    }
  };

  const markAsSolved = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("messages")
        .update({ solved: true })
        .eq("id", messageId);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, solved: true } : msg
        ));
        toast({
          title: "Success",
          description: "Message marked as solved",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark message as solved",
        variant: "destructive",
      });
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("messages")
        .delete()
        .eq("id", messageId);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        setSelectedMessage(null);
        toast({
          title: "Success",
          description: "Message deleted successfully",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive",
      });
    }
  };

  const handleMessageClick = (message: Message) => {
    setSelectedMessage(message);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(msg => 
    activeTab === 'new' ? !msg.solved : msg.solved
  );
  
  const unreadCount = messages.filter(msg => !msg.read && !msg.solved).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Admin Messages {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {unreadCount} unread
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Manage customer messages and custom work requests.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="new" value={activeTab} onValueChange={(value) => setActiveTab(value as 'new' | 'solved')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              New Messages ({filteredMessages.length})
            </TabsTrigger>
            <TabsTrigger value="solved" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Solved ({messages.filter(m => m.solved).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-[60vh]">
              {/* Messages List */}
              <div className="space-y-2">
                <ScrollArea className="h-full">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <p className="text-muted-foreground">Loading messages...</p>
                    </div>
                  ) : filteredMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-center">
                        <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">
                          {activeTab === 'new' ? 'No new messages' : 'No solved messages'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    filteredMessages.map((message) => (
                      <Card 
                        key={message.id}
                        className={`mb-2 cursor-pointer transition-colors hover:bg-muted/50 ${
                          selectedMessage?.id === message.id ? 'ring-2 ring-primary' : ''
                        } ${!message.read && !message.solved ? 'border-l-4 border-l-primary' : ''}`}
                        onClick={() => handleMessageClick(message)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium">{message.name}</h4>
                                {!message.read && !message.solved && (
                                  <Badge variant="secondary" className="text-xs">
                                    New
                                  </Badge>
                                )}
                                {message.solved && (
                                  <Badge variant="outline" className="text-xs text-green-600">
                                    Solved
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{message.email}</p>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(message.created_at)}
                            </p>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <p className="text-sm font-medium mb-1">{message.subject}</p>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {message.message}
                          </p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </ScrollArea>
              </div>

              {/* Message Detail */}
              <div className="border-l pl-4">
                {selectedMessage ? (
                  <ScrollArea className="h-full">
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <User className="h-4 w-4" />
                          <h3 className="font-semibold">{selectedMessage.name}</h3>
                          {selectedMessage.solved && (
                            <Badge variant="outline" className="text-green-600">
                              Solved
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{selectedMessage.email}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatDate(selectedMessage.created_at)}
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <h4 className="font-medium text-sm mb-2">Subject</h4>
                        <p className="text-sm">{selectedMessage.subject}</p>
                      </div>

                      <Separator />

                      <div>
                        <h4 className="font-medium text-sm mb-2">Message</h4>
                        <div className="bg-muted/50 rounded-lg p-4">
                          <p className="text-sm whitespace-pre-wrap">{selectedMessage.message}</p>
                        </div>
                      </div>

                      <div className="pt-4 space-y-2">
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => window.open(`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`)}
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Reply via Email
                          </Button>
                          {!selectedMessage.solved && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => markAsSolved(selectedMessage.id)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Solved
                            </Button>
                          )}
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
                              deleteMessage(selectedMessage.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Message
                        </Button>
                      </div>
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">Select a message to view details</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};